import { createClient, type Session } from "@supabase/supabase-js";
import "@fontsource-variable/outfit";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { formatLocalDateTime } from "@topzle/locale";
import {
  ArrowLeft,
  Boxes,
  CalendarClock,
  CheckCircle2,
  CircleAlert,
  Eye,
  Flag,
  Gamepad2,
  Headphones,
  KeyRound,
  LayoutDashboard,
  LoaderCircle,
  LogOut,
  Mail,
  MessageSquareText,
  PauseCircle,
  PlayCircle,
  Radio,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, NavLink } from "react-router-dom";
import { BrandMark } from "./BrandMark";
import "./styles.css";
import { TurnstileWidget, type TurnstileWidgetHandle } from "./TurnstileWidget";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;
async function api<T>(path: string, session: Session, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${session.access_token}`);
  if (init.body) headers.set("Content-Type", "application/json");
  const response = await fetch(
    `${import.meta.env.VITE_API_URL ?? "http://localhost:8787"}${path}`,
    { ...init, headers },
  );
  const body = (await response.json()) as { ok: boolean; data?: T; error?: { message: string } };
  if (!response.ok || !body.ok || !body.data)
    throw new Error(body.error?.message ?? "Admin request failed");
  return body.data;
}
async function unauthenticatedApi<T>(path: string, input: unknown): Promise<T> {
  const response = await fetch(
    `${import.meta.env.VITE_API_URL ?? "http://localhost:8787"}${path}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  const body = (await response.json()) as {
    ok: boolean;
    data?: T;
    error?: { message: string };
  };
  if (!response.ok || !body.ok || !body.data)
    throw new Error(body.error?.message ?? "Admin sign-in failed");
  return body.data;
}
async function installAdminSession(session: {
  access_token: string;
  refresh_token: string;
}): Promise<Session> {
  if (!supabase) throw new Error("Configure Supabase environment variables.");
  const { data, error } = await supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });
  if (error || !data.session) throw new Error(error?.message ?? "Admin session was not created");
  return data.session;
}
function Login({ onSession }: { onSession: (session: Session) => void }) {
  const [magicLink] = useState(() => {
    const search = new URLSearchParams(window.location.search);
    const challengeId = search.get("challenge");
    const token = search.get("token");
    if (!challengeId || !token || search.get("audience") !== "admin") return null;
    return { challengeId, token };
  });
  const [magicStatus, setMagicStatus] = useState<
    | { phase: "checking"; message: string }
    | { phase: "opening"; message: string }
    | { phase: "error"; message: string }
    | null
  >(() =>
    magicLink ? { phase: "checking", message: "Checking your secure administrator link…" } : null,
  );
  const [step, setStep] = useState<"identifier" | "code">("identifier");
  const [identifier, setIdentifier] = useState("");
  const [channel, setChannel] = useState<"email" | "phone" | null>(null);
  const [code, setCode] = useState("");
  const [otpState, setOtpState] = useState<"idle" | "validating" | "success" | "error">("idle");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const magicAttempted = useRef(false);
  const handleCaptcha = useCallback((token: string | null) => setCaptchaToken(token), []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setInterval(
      () => setResendIn((current) => Math.max(0, current - 1)),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, [resendIn]);

  useEffect(() => {
    if (magicAttempted.current || !magicLink) return;
    magicAttempted.current = true;
    window.history.replaceState(window.history.state, "", window.location.pathname);
    void unauthenticatedApi<{
      session: { access_token: string; refresh_token: string };
    }>("/api/auth/magic/verify", {
      ...magicLink,
      audience: "admin",
    })
      .then(({ session }) => installAdminSession(session))
      .then((session) => {
        setMagicStatus({
          phase: "opening",
          message: "Link confirmed. Opening Topzle Admin…",
        });
        onSession(session);
      })
      .catch((authError) => {
        setMagicStatus({
          phase: "error",
          message:
            authError instanceof Error
              ? authError.message
              : "This administrator link is invalid or has expired.",
        });
      });
  }, [magicLink, onSession]);

  if (magicStatus) {
    const failed = magicStatus.phase === "error";
    return (
      <main className="admin-magic-page" aria-live="polite">
        <section className={`admin-magic-panel${failed ? " failed" : ""}`}>
          <BrandMark />
          <div className="admin-magic-indicator" aria-hidden="true">
            {failed ? (
              <CircleAlert />
            ) : magicStatus.phase === "opening" ? (
              <CheckCircle2 />
            ) : (
              <LoaderCircle className="admin-magic-spinner" />
            )}
          </div>
          <h1>
            {failed
              ? "This link didn’t work"
              : magicStatus.phase === "opening"
                ? "Access confirmed"
                : "Signing you in"}
          </h1>
          <p>{magicStatus.message}</p>
          {failed && (
            <a className="admin-magic-retry" href="/">
              Request a new administrator link
            </a>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="login">
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (!supabase) {
            setError("Configure Supabase environment variables.");
            return;
          }
          if (!captchaToken) {
            setError("Complete the security check.");
            return;
          }
          setError("");
          setNotice("");
          setSubmitting(true);
          try {
            if (step === "identifier") {
              const result = await unauthenticatedApi<{
                channel: "email" | "phone";
                destination: string;
                resendAfterSeconds: number;
              }>("/api/auth/request", {
                identifier,
                audience: "admin",
                turnstileToken: captchaToken,
              });
              setChannel(result.channel);
              setStep("code");
              setCode("");
              setOtpState("idle");
              setResendIn(result.resendAfterSeconds);
              setNotice(
                result.channel === "email"
                  ? `A magic link and code were sent to ${result.destination}.`
                  : `A code was sent to ${result.destination}.`,
              );
            } else {
              if (!/^\d{6}$/.test(code)) throw new Error("Enter the six-digit sign-in code.");
              setOtpState("validating");
              const result = await unauthenticatedApi<{
                session: { access_token: string; refresh_token: string };
              }>("/api/auth/verify", {
                identifier,
                code,
                audience: "admin",
              });
              const session = await installAdminSession(result.session);
              setOtpState("success");
              if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches)
                await new Promise((resolve) => window.setTimeout(resolve, 700));
              onSession(session);
            }
          } catch (authError) {
            setError(authError instanceof Error ? authError.message : "Admin sign-in failed");
            if (step === "identifier") turnstileRef.current?.reset();
            else setOtpState("error");
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <BrandMark />
        <small>TOPZLE OPERATIONS</small>
        <h1>Secure control room</h1>
        <p>
          {step === "identifier"
            ? "Use the email or mobile number attached to your administrator account."
            : channel === "email"
              ? "Enter the emailed code, or open the magic link from the same email."
              : "Enter the code sent to your administrator phone."}
        </p>
        {step === "identifier" ? (
          <>
            <label>
              Admin email or mobile
              <input
                type="text"
                inputMode="email"
                autoComplete="username"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value.slice(0, 254))}
                placeholder="admin@example.com or +919876543210"
                required
              />
            </label>
            <TurnstileWidget ref={turnstileRef} onToken={handleCaptcha} />
          </>
        ) : (
          <label>
            One-time code
            <span className={`admin-otp-safe ${otpState}`}>
              <input
                className="admin-otp-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                  setOtpState("idle");
                }}
                required
              />
              <span className="admin-otp-digits" aria-hidden="true">
                {["one", "two", "three", "four", "five", "six"].map((slot, index) => (
                  <i key={slot} style={{ animationDelay: `${index * 75}ms` }}>
                    {code[index] || "•"}
                  </i>
                ))}
              </span>
            </span>
          </label>
        )}
        <button type="submit" disabled={(step === "identifier" && !captchaToken) || submitting}>
          {submitting ? <span className="button-spinner" aria-hidden="true" /> : null}
          {submitting
            ? step === "identifier"
              ? "Sending…"
              : "Verifying…"
            : step === "identifier"
              ? "Continue securely"
              : "Verify and sign in"}
        </button>
        {step === "code" && (
          <div className="admin-auth-actions">
            <button
              type="button"
              onClick={() => {
                setStep("identifier");
                setChannel(null);
                setCode("");
                setOtpState("idle");
                setError("");
                setNotice("");
                turnstileRef.current?.reset();
              }}
              disabled={submitting}
            >
              <ArrowLeft /> Change
            </button>
            <button
              type="button"
              disabled={submitting || resendIn > 0}
              onClick={() => {
                setStep("identifier");
                setCode("");
                setOtpState("idle");
                setNotice("Complete the fresh security check to send another sign-in message.");
                turnstileRef.current?.reset();
              }}
            >
              {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend"}
            </button>
          </div>
        )}
        {notice && <div className="admin-auth-notice">{notice}</div>}
        {error && <div className="error">{error}</div>}
        <span>
          {step === "identifier" ? <Mail /> : <KeyRound />} Admin role and every action are verified
        </span>
      </form>
    </main>
  );
}
function AccessDenied({ session }: { session: Session }) {
  return (
    <main className="login">
      <section>
        <BrandMark />
        <small>TOPZLE OPERATIONS</small>
        <h1>Admin access required</h1>
        <p>
          {session.user.email} is authenticated, but its trusted app metadata does not contain an
          administrator role. Ask a super administrator to grant access, then sign in again.
        </p>
        <button type="button" onClick={() => void supabase?.auth.signOut()}>
          Sign out
        </button>
      </section>
    </main>
  );
}
type ReportRow = {
  id: string;
  target_type: string;
  target_id: string;
  category: string;
  description?: string;
  status: string;
  priority?: string;
  resolution?: string | null;
  created_at: string;
};
type UserRow = {
  id: string;
  display_name: string;
  email: string | null;
  phone_e164: string | null;
  age_band: string;
  parental_consent_status: string;
  onboarding_completed_at: string | null;
  status: string;
  last_seen_at: string | null;
  created_at: string;
  suspended_until: string | null;
  suspended_permanently: boolean;
  suspension_reason: string | null;
};
type RoomRow = {
  id: string;
  short_code: string;
  title: string;
  status: string;
  visibility: string;
  age_category: string;
  current_game_id: string | null;
  created_at: string;
  updated_at: string;
};
type RoomCleanupPreview = {
  olderThanDays: number;
  cutoff: string;
  candidateCount: number;
  batchLimit: number;
  candidates: Array<{
    id: string;
    short_code: string;
    title: string;
    status: string;
    current_game_id: string | null;
    created_at: string;
    updated_at: string;
  }>;
};
type RoomCleanupResult = {
  cutoff: string;
  requested: number;
  cleared: number;
  skipped: Array<{ id: string; title: string; reason: string }>;
  remainingMayExist: boolean;
};
type SafetyData = {
  youngPlayers: Array<{
    id: string;
    display_name: string;
    age_band: string;
    parental_consent_status: string;
    status: string;
    created_at: string;
    last_seen_at: string | null;
  }>;
  openReports: ReportRow[];
};
type AnalyticsData = {
  range: { start: string; end: string };
  totalGames: number;
  completedGames: number;
  completionRate: number;
  dau: number;
  mau: number;
  roomsCreated: number;
  byGame: Array<{ game_id: string; name: string; plays: number; completed: number }>;
  daily: Array<{ date: string; games: number; rooms: number; active_users: number }>;
};
type ControlRow = {
  id?: string;
  key?: string;
  name?: string;
  description?: string;
  enabled: boolean;
  maintenance?: boolean;
  kill_switch?: boolean;
  disabled_until: string | null;
  disabled_reason: string | null;
};
type ControlsData = {
  games: ControlRow[];
  features: ControlRow[];
  mediaProvider: "p2p" | "cloudflare_realtime";
  canManage: boolean;
};
type LiveRoomData = {
  snapshot: {
    roomId: string;
    code: string;
    currentGameId: string | null;
    participants: Array<{
      id: string;
      displayName: string;
      connected: boolean;
      role: string;
    }>;
  };
  recentChat: Array<{
    id: string;
    senderId?: string;
    serverTimestamp?: number;
    payload: { text?: string };
  }>;
  media: Array<{
    id: string;
    displayName: string;
    connected: boolean;
    microphone: boolean;
    camera: boolean;
    speaking: boolean;
    connectionQuality: string;
  }>;
};
type SupportRow = {
  id: string;
  kind: string;
  subject: string | null;
  description: string;
  contact_email: string | null;
  status: string;
  priority: string;
  assigned_to: string | null;
  replied_at: string | null;
  evidence?: {
    visitor_context?: {
      country_code?: string | null;
      city?: string | null;
      region?: string | null;
      timezone?: string | null;
      source?: string | null;
      contact_phone?: string | null;
      client_context?: {
        source?: string;
        path?: string;
        roomId?: string;
        roomCode?: string;
        gameId?: string;
        userAgent?: string;
        platform?: string;
        language?: string;
        browserTimeZone?: string;
        viewportWidth?: number;
        viewportHeight?: number;
        screenWidth?: number;
        screenHeight?: number;
        pixelRatio?: number;
        touchPoints?: number;
        connectionType?: string;
        currencyCode?: string | null;
      };
    };
  } | null;
  created_at: string;
  updated_at: string;
};
type SupportMessage = {
  id: string;
  author_type: "customer" | "agent" | "system";
  author_id: string | null;
  body: string;
  channel: "web" | "admin" | "email" | "system";
  email_to: string | null;
  email_message_id: string | null;
  email_status: "not_applicable" | "queued" | "accepted" | "failed";
  email_error: string | null;
  created_at: string;
};
type SupportDetail = { ticket: SupportRow; messages: SupportMessage[] };
type AdministratorsData = {
  administrators: Array<{
    user_id: string;
    active: boolean;
    created_at: string;
    profiles: { display_name: string; email: string | null; last_seen_at: string | null };
    admin_roles: { key: string; description: string; permissions: string[] };
  }>;
  roles: Array<{ id: string; key: string; description: string; permissions: string[] }>;
};

function StatePill({ value }: { value: string }) {
  return (
    <span className={`state-pill ${value.replaceAll("_", "-")}`}>{value.replaceAll("_", " ")}</span>
  );
}

function UsersPage({ session }: { session: Session }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<UserRow | null>(null);
  const [suspensionType, setSuspensionType] = useState<"temporary" | "permanent" | "clear">(
    "temporary",
  );
  const [duration, setDuration] = useState("1440");
  const [reason, setReason] = useState("");
  const [customEmail, setCustomEmail] = useState("");
  const query = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api<UserRow[]>("/api/admin/users", session),
  });
  const rows = (query.data ?? []).filter((row) =>
    `${row.display_name} ${row.email ?? ""} ${row.phone_e164 ?? ""}`
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase()),
  );
  const suspension = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error("Choose a player");
      return api(`/api/admin/users/${selected.id}/suspension`, session, {
        method: "POST",
        body: JSON.stringify({
          action: suspensionType,
          ...(suspensionType === "temporary" ? { durationMinutes: Number(duration) } : {}),
          reason,
          ...(customEmail.trim() ? { customEmail } : {}),
        }),
      });
    },
    onSuccess: async () => {
      setSelected(null);
      setReason("");
      setCustomEmail("");
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
  return (
    <section>
      <Header
        eyebrow="ACCOUNTS"
        title="Players"
        copy="See onboarding, verification and age-safety state without exposing full phone numbers."
      />
      <div className="toolbar">
        <div>
          <Search />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Find a player"
          />
        </div>
        <span className="toolbar-count">{rows.length} shown</span>
      </div>
      {query.error && <div className="error">{query.error.message}</div>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Player</th>
              <th>Phone</th>
              <th>Age mode</th>
              <th>Consent</th>
              <th>Onboarding</th>
              <th>Last active</th>
              <th>Account</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.display_name}</strong>
                  <small className="cell-sub">{row.email ?? "No email"}</small>
                </td>
                <td>{row.phone_e164 ?? "—"}</td>
                <td>
                  <StatePill value={row.age_band} />
                </td>
                <td>
                  <StatePill value={row.parental_consent_status} />
                </td>
                <td>
                  {row.onboarding_completed_at ? (
                    <span className="verified">
                      <CheckCircle2 /> Complete
                    </span>
                  ) : (
                    "Incomplete"
                  )}
                </td>
                <td>{row.last_seen_at ? formatLocalDateTime(row.last_seen_at) : "Never"}</td>
                <td>
                  {row.suspended_permanently ? (
                    <StatePill value="suspended" />
                  ) : row.suspended_until && new Date(row.suspended_until) > new Date() ? (
                    <StatePill value="temporarily suspended" />
                  ) : (
                    <StatePill value="active" />
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="secondary-action"
                    onClick={() => {
                      setSelected(row);
                      setSuspensionType(
                        row.suspended_permanently ||
                          (row.suspended_until && new Date(row.suspended_until) > new Date())
                          ? "clear"
                          : "temporary",
                      );
                      setReason("");
                    }}
                  >
                    Manage
                  </button>
                </td>
              </tr>
            ))}
            {!query.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <div className="empty">
                    <Users />
                    No matching players.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="modal-backdrop">
          <section
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Manage ${selected.display_name}`}
          >
            <button className="modal-close" type="button" onClick={() => setSelected(null)}>
              <X />
            </button>
            <small>ACCOUNT MODERATION</small>
            <h2>{selected.display_name}</h2>
            <p>
              Access is blocked immediately and the action is audited. The player receives the
              message below by email when an address is available.
            </p>
            <label>
              Action
              <select
                value={suspensionType}
                onChange={(event) => setSuspensionType(event.target.value as typeof suspensionType)}
              >
                <option value="temporary">Temporary suspension</option>
                <option value="permanent">Permanent suspension</option>
                <option value="clear">Restore account</option>
              </select>
            </label>
            {suspensionType === "temporary" && (
              <label>
                Duration
                <select value={duration} onChange={(event) => setDuration(event.target.value)}>
                  <option value="60">1 hour</option>
                  <option value="1440">1 day</option>
                  <option value="10080">7 days</option>
                  <option value="43200">30 days</option>
                </select>
              </label>
            )}
            <label>
              Internal reason
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Explain why this action is necessary"
              />
            </label>
            <label>
              Email message <span>optional</span>
              <textarea
                value={customEmail}
                onChange={(event) => setCustomEmail(event.target.value)}
                placeholder="Add a clear, respectful message for the player"
              />
            </label>
            {suspension.error && <div className="error">{suspension.error.message}</div>}
            <button
              type="button"
              className="primary-action"
              disabled={suspension.isPending || reason.trim().length < 3}
              onClick={() => suspension.mutate()}
            >
              {suspension.isPending ? <LoaderCircle className="spin" /> : <ShieldCheck />}
              {suspensionType === "clear" ? "Restore account" : "Apply suspension"}
            </button>
          </section>
        </div>
      )}
    </section>
  );
}

function RoomsPage({ session, role }: { session: Session; role: string }) {
  const [selectedRoom, setSelectedRoom] = useState<RoomRow | null>(null);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupDays, setCleanupDays] = useState("30");
  const [cleanupReason, setCleanupReason] = useState("Routine removal of old inactive rooms");
  const [cleanupConfirmation, setCleanupConfirmation] = useState("");
  const queryClient = useQueryClient();
  const canClearRooms = ["super_admin", "operations_admin", "moderator"].includes(role);
  const playerSiteOrigin =
    import.meta.env.VITE_WEB_URL ??
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
      ? "http://localhost:5173"
      : "https://topzle.com");
  const query = useQuery({
    queryKey: ["admin-rooms"],
    queryFn: () => api<RoomRow[]>("/api/admin/rooms", session),
    refetchInterval: 30_000,
  });
  const live = useQuery({
    queryKey: ["admin-room-live", selectedRoom?.id],
    queryFn: () => api<LiveRoomData>(`/api/admin/rooms/${selectedRoom?.id}/live`, session),
    enabled: Boolean(selectedRoom),
    refetchInterval: 3_000,
  });
  const cleanupPreview = useQuery({
    queryKey: ["admin-room-cleanup", cleanupDays],
    queryFn: () =>
      api<RoomCleanupPreview>(
        `/api/admin/rooms/cleanup?olderThanDays=${encodeURIComponent(cleanupDays)}`,
        session,
      ),
    enabled: cleanupOpen && canClearRooms,
  });
  const cleanup = useMutation({
    mutationFn: () =>
      api<RoomCleanupResult>("/api/admin/rooms/cleanup", session, {
        method: "POST",
        body: JSON.stringify({
          olderThanDays: Number(cleanupDays),
          reason: cleanupReason.trim(),
          confirmation: cleanupConfirmation,
        }),
      }),
    onSuccess: async () => {
      setCleanupConfirmation("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-rooms"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-room-cleanup"] }),
        queryClient.invalidateQueries({ queryKey: ["overview"] }),
      ]);
    },
  });
  return (
    <section>
      <Header
        eyebrow="LIVE ROOMS"
        title="Room operations"
        copy="Recent rooms, current game and safety category. This view refreshes every 30 seconds."
      />
      <div className="room-operations-bar">
        <div>
          <strong>Room retention</strong>
          <span>
            Clear private room state after it is no longer needed while keeping aggregate game
            totals.
          </span>
        </div>
        {canClearRooms && (
          <button type="button" onClick={() => setCleanupOpen(true)}>
            <Trash2 /> Clear old rooms
          </button>
        )}
      </div>
      {query.error && <div className="error">{query.error.message}</div>}
      <div className="room-admin-grid">
        {(query.data ?? []).map((room) => (
          <article key={room.id}>
            <div>
              <span className="room-code">{room.short_code}</span>
              <StatePill value={room.status} />
            </div>
            <h2>{room.title}</h2>
            <p>{room.current_game_id?.replaceAll("-", " ") ?? "Waiting for a game"}</p>
            <footer>
              <span>
                <ShieldCheck /> {room.age_category}
              </span>
              <span>
                <CalendarClock /> {formatLocalDateTime(room.updated_at)}
              </span>
              {["waiting", "active", "paused", "reconnecting", "restoring"].includes(
                room.status,
              ) && (
                <button type="button" onClick={() => setSelectedRoom(room)}>
                  <Eye /> Inspect live
                </button>
              )}
            </footer>
          </article>
        ))}
        {!query.isLoading && !query.data?.length && (
          <div className="empty large">
            <Boxes />
            <h2>No recent rooms</h2>
            <p>Rooms will appear after the production database is connected.</p>
          </div>
        )}
      </div>
      {selectedRoom && (
        <div className="modal-backdrop">
          <section
            className="admin-modal live-room-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Live room ${selectedRoom.short_code}`}
          >
            <button className="modal-close" type="button" onClick={() => setSelectedRoom(null)}>
              <X />
            </button>
            <small>VISIBLE MODERATOR VIEW · AUDITED</small>
            <h2>{selectedRoom.title}</h2>
            <p>
              This shows spectator-safe game state, recent room chat, and call status. It never
              opens a hidden microphone, camera, or private game state.
            </p>
            <a
              className="visible-spectator-link"
              href={`${playerSiteOrigin}/join/${selectedRoom.short_code}?spectator=1`}
              target="_blank"
              rel="noreferrer"
            >
              <Eye /> Join as a visible spectator
            </a>
            {live.error && <div className="error">{live.error.message}</div>}
            <div className="live-room-columns">
              <div>
                <h3>
                  <Radio /> Players & call
                </h3>
                {(live.data?.media ?? []).map((participant) => (
                  <article className="live-participant" key={participant.id}>
                    <span className={participant.connected ? "presence online" : "presence"} />
                    <div>
                      <strong>{participant.displayName}</strong>
                      <small>{participant.connectionQuality}</small>
                    </div>
                    <span>{participant.microphone ? "Mic on" : "Muted"}</span>
                    <span>{participant.camera ? "Camera on" : "Camera off"}</span>
                  </article>
                ))}
              </div>
              <div>
                <h3>
                  <MessageSquareText /> Recent chat
                </h3>
                <div className="live-chat">
                  {(live.data?.recentChat ?? []).map((message) => {
                    const sender = live.data?.snapshot.participants.find(
                      (participant) => participant.id === message.senderId,
                    );
                    return (
                      <article key={message.id}>
                        <strong>{sender?.displayName ?? "Player"}</strong>
                        <p>{message.payload.text ?? "Message"}</p>
                        <small>
                          {message.serverTimestamp
                            ? formatLocalDateTime(new Date(message.serverTimestamp).toISOString())
                            : ""}
                        </small>
                      </article>
                    );
                  })}
                  {!live.isLoading && !live.data?.recentChat.length && <p>No recent messages.</p>}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
      {cleanupOpen && (
        <div className="modal-backdrop">
          <section
            className="admin-modal room-cleanup-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Clear old inactive rooms"
          >
            <button
              className="modal-close"
              type="button"
              disabled={cleanup.isPending}
              onClick={() => setCleanupOpen(false)}
            >
              <X />
            </button>
            <small>PRIVACY & STORAGE</small>
            <h2>Clear old inactive rooms</h2>
            <p>
              Connected or recently updated rooms are always skipped. This permanently removes chat,
              room events, invitations, memberships, snapshots, and live room storage. Aggregate
              game results remain available for analytics.
            </p>
            <div className="cleanup-settings">
              <label>
                Inactive for at least
                <select
                  value={cleanupDays}
                  disabled={cleanup.isPending}
                  onChange={(event) => {
                    setCleanupDays(event.target.value);
                    cleanup.reset();
                  }}
                >
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">1 year</option>
                </select>
              </label>
              <label>
                Audit reason
                <textarea
                  value={cleanupReason}
                  disabled={cleanup.isPending}
                  onChange={(event) => setCleanupReason(event.target.value)}
                  maxLength={500}
                />
              </label>
            </div>
            {cleanupPreview.isLoading && (
              <div className="cleanup-loading">
                <LoaderCircle className="spin" /> Checking eligible rooms…
              </div>
            )}
            {cleanupPreview.error && <div className="error">{cleanupPreview.error.message}</div>}
            {cleanupPreview.data && (
              <>
                <div className="cleanup-summary">
                  <strong>{cleanupPreview.data.candidateCount}</strong>
                  <span>
                    database candidate
                    {cleanupPreview.data.candidateCount === 1 ? "" : "s"} older than{" "}
                    {cleanupPreview.data.olderThanDays} days
                  </span>
                  {cleanupPreview.data.candidateCount > cleanupPreview.data.batchLimit && (
                    <small>
                      Up to {cleanupPreview.data.batchLimit} rooms are cleared per batch.
                    </small>
                  )}
                </div>
                {cleanupPreview.data.candidates.length > 0 && (
                  <div className="cleanup-candidates">
                    {cleanupPreview.data.candidates.slice(0, 8).map((room) => (
                      <article key={room.id}>
                        <div>
                          <strong>{room.title}</strong>
                          <small>
                            {room.short_code} · {room.status}
                          </small>
                        </div>
                        <time>{formatLocalDateTime(room.updated_at)}</time>
                      </article>
                    ))}
                    {cleanupPreview.data.candidateCount > 8 && (
                      <small>+ {cleanupPreview.data.candidateCount - 8} more candidates</small>
                    )}
                  </div>
                )}
              </>
            )}
            {cleanup.data && (
              <div className="cleanup-result" role="status">
                <CheckCircle2 />
                <div>
                  <strong>{cleanup.data.cleared} rooms cleared</strong>
                  <span>
                    {cleanup.data.skipped.length} skipped because they were connected, recent, or
                    unavailable.
                  </span>
                  {cleanup.data.remainingMayExist && (
                    <small>More old rooms may remain. Preview and run another batch.</small>
                  )}
                </div>
              </div>
            )}
            {cleanup.error && <div className="error">{cleanup.error.message}</div>}
            {(cleanupPreview.data?.candidateCount ?? 0) > 0 && (
              <label className="cleanup-confirmation">
                Type <strong>CLEAR OLD ROOMS</strong> to confirm
                <input
                  value={cleanupConfirmation}
                  disabled={cleanup.isPending}
                  onChange={(event) => setCleanupConfirmation(event.target.value)}
                  autoComplete="off"
                />
              </label>
            )}
            <div className="cleanup-actions">
              <button
                type="button"
                disabled={cleanup.isPending}
                onClick={() => setCleanupOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-action"
                disabled={
                  cleanup.isPending ||
                  cleanupPreview.isLoading ||
                  (cleanupPreview.data?.candidateCount ?? 0) === 0 ||
                  cleanupReason.trim().length < 3 ||
                  cleanupConfirmation !== "CLEAR OLD ROOMS"
                }
                onClick={() => cleanup.mutate()}
              >
                {cleanup.isPending ? <LoaderCircle className="spin" /> : <Trash2 />}
                {cleanup.isPending ? "Clearing rooms…" : "Clear eligible rooms"}
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function SafetyCenter({ session }: { session: Session }) {
  const query = useQuery({
    queryKey: ["admin-safety"],
    queryFn: () => api<SafetyData>("/api/admin/safety", session),
  });
  const childCount =
    query.data?.youngPlayers.filter((player) => player.age_band === "child").length ?? 0;
  const consentQueue =
    query.data?.youngPlayers.filter((player) =>
      ["required", "pending"].includes(player.parental_consent_status),
    ).length ?? 0;
  return (
    <section>
      <Header
        eyebrow="TRUST & SAFETY"
        title="Young player safety"
        copy="Review the consent queue and open reports. Phone, birth date and private calls are not shown here."
      />
      <div className="safety-summary">
        <article>
          <Users />
          <strong>{query.data?.youngPlayers.length ?? "—"}</strong>
          <span>Young accounts</span>
        </article>
        <article>
          <ShieldCheck />
          <strong>{childCount}</strong>
          <span>Child mode</span>
        </article>
        <article>
          <Flag />
          <strong>{consentQueue}</strong>
          <span>Consent reviews</span>
        </article>
        <article>
          <MessageSquareText />
          <strong>{query.data?.openReports.length ?? "—"}</strong>
          <span>Open reports</span>
        </article>
      </div>
      {query.error && <div className="error">{query.error.message}</div>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Player</th>
              <th>Age mode</th>
              <th>Consent</th>
              <th>Account</th>
              <th>Last active</th>
            </tr>
          </thead>
          <tbody>
            {(query.data?.youngPlayers ?? []).map((player) => (
              <tr key={player.id}>
                <td>
                  <strong>{player.display_name}</strong>
                </td>
                <td>
                  <StatePill value={player.age_band} />
                </td>
                <td>
                  <StatePill value={player.parental_consent_status} />
                </td>
                <td>
                  <StatePill value={player.status} />
                </td>
                <td>{player.last_seen_at ? formatLocalDateTime(player.last_seen_at) : "Never"}</td>
              </tr>
            ))}
            {!query.isLoading && !query.data?.youngPlayers.length && (
              <tr>
                <td colSpan={5}>
                  <div className="empty">
                    <ShieldCheck />
                    No young-player reviews are waiting.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function OperationsPage({ session }: { session: Session }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<{ kind: "game" | "feature"; row: ControlRow } | null>(
    null,
  );
  const [duration, setDuration] = useState("60");
  const [reason, setReason] = useState("");
  const query = useQuery({
    queryKey: ["admin-controls"],
    queryFn: () => api<ControlsData>("/api/admin/controls", session),
  });
  const update = useMutation({
    mutationFn: (input: {
      kind: "game" | "feature";
      key: string;
      enabled: boolean;
      durationMinutes?: number;
      reason?: string;
    }) =>
      api(`/api/admin/controls/${input.kind}/${input.key}`, session, {
        method: "PATCH",
        body: JSON.stringify({
          enabled: input.enabled,
          ...(input.durationMinutes ? { durationMinutes: input.durationMinutes } : {}),
          ...(input.reason ? { reason: input.reason } : {}),
        }),
      }),
    onSuccess: async () => {
      setSelected(null);
      setReason("");
      await queryClient.invalidateQueries({ queryKey: ["admin-controls"] });
    },
  });
  const updateMediaProvider = useMutation({
    mutationFn: (provider: ControlsData["mediaProvider"]) =>
      api<{ provider: ControlsData["mediaProvider"] }>("/api/admin/media-provider", session, {
        method: "PATCH",
        body: JSON.stringify({ provider }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-controls"] });
    },
  });
  const isAvailable = (row: ControlRow) =>
    row.enabled &&
    !row.maintenance &&
    !row.kill_switch &&
    (!row.disabled_until || new Date(row.disabled_until) <= new Date());
  const cards = [
    ...(query.data?.features ?? []).map((row) => ({ kind: "feature" as const, row })),
    ...(query.data?.games ?? []).map((row) => ({ kind: "game" as const, row })),
  ];
  return (
    <section>
      <Header
        eyebrow="PLATFORM CONTROLS"
        title="Keep play healthy"
        copy="Pause a game, calls, or text chat immediately or for a scheduled period. Changes take effect in the Worker and are audited."
      />
      {query.error && <div className="error">{query.error.message}</div>}
      {query.data && (
        <article className="media-provider-control">
          <div>
            <small>CALL ROUTING</small>
            <h2>Audio and video provider</h2>
            <p>
              P2P sends encrypted media directly between player devices. Cloudflare Realtime is the
              managed fallback for networks where direct connections are unreliable.
            </p>
          </div>
          <div className="media-provider-options" role="radiogroup" aria-label="Call provider">
            {(
              [
                ["p2p", "Direct P2P", "Lowest server bandwidth"],
                ["cloudflare_realtime", "Cloudflare Realtime", "Managed relay and SFU"],
              ] as const
            ).map(([provider, label, detail]) => (
              <button
                key={provider}
                type="button"
                aria-pressed={query.data.mediaProvider === provider}
                className={query.data.mediaProvider === provider ? "selected" : ""}
                disabled={!query.data.canManage || updateMediaProvider.isPending}
                onClick={() => updateMediaProvider.mutate(provider)}
              >
                {updateMediaProvider.isPending && updateMediaProvider.variables === provider ? (
                  <LoaderCircle className="spin" />
                ) : (
                  <Headphones />
                )}
                <span>
                  <strong>{label}</strong>
                  <small>{detail}</small>
                </span>
              </button>
            ))}
          </div>
          {updateMediaProvider.error && (
            <div className="error">{updateMediaProvider.error.message}</div>
          )}
        </article>
      )}
      <div className="control-grid">
        {cards.map(({ kind, row }) => {
          const key = row.id ?? row.key ?? "";
          const available = isAvailable(row);
          return (
            <article key={`${kind}-${key}`}>
              <span className={`control-icon ${available ? "available" : "paused"}`}>
                {kind === "game" ? (
                  <Gamepad2 />
                ) : key === "calls" || key.endsWith("_calls") ? (
                  <Headphones />
                ) : (
                  <MessageSquareText />
                )}
              </span>
              <div>
                <small>{kind}</small>
                <h2>
                  {row.name ??
                    (key === "anonymous_calls"
                      ? "Anonymous call access"
                      : key === "unverified_calls"
                        ? "Unverified account call access"
                        : key.replaceAll("_", " "))}
                </h2>
                <p>
                  {available
                    ? "Available"
                    : row.disabled_until
                      ? `Paused until ${formatLocalDateTime(row.disabled_until)}`
                      : "Paused until restored"}
                </p>
                {(row.disabled_reason || row.description) && (
                  <span>{row.disabled_reason ?? row.description}</span>
                )}
              </div>
              {query.data?.canManage && (
                <button
                  type="button"
                  disabled={update.isPending}
                  onClick={() => {
                    if (available) setSelected({ kind, row });
                    else
                      update.mutate({
                        kind,
                        key,
                        enabled: true,
                      });
                  }}
                >
                  {available ? <PauseCircle /> : <PlayCircle />}
                  {available ? "Pause" : "Restore"}
                </button>
              )}
            </article>
          );
        })}
      </div>
      {selected && (
        <div className="modal-backdrop">
          <section className="admin-modal" role="dialog" aria-modal="true">
            <button className="modal-close" type="button" onClick={() => setSelected(null)}>
              <X />
            </button>
            <small>PAUSE CONTROL</small>
            <h2>{selected.row.name ?? selected.row.key?.replaceAll("_", " ")}</h2>
            <label>
              Duration
              <select value={duration} onChange={(event) => setDuration(event.target.value)}>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="1440">1 day</option>
                <option value="10080">7 days</option>
                <option value="0">Until manually restored</option>
              </select>
            </label>
            <label>
              Reason shown to players
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="For example: Scheduled maintenance"
              />
            </label>
            <button
              type="button"
              className="danger-action"
              disabled={update.isPending || reason.trim().length < 3}
              onClick={() =>
                update.mutate({
                  kind: selected.kind,
                  key: selected.row.id ?? selected.row.key ?? "",
                  enabled: false,
                  ...(Number(duration) ? { durationMinutes: Number(duration) } : {}),
                  reason,
                })
              }
            >
              {update.isPending ? <LoaderCircle className="spin" /> : <PauseCircle />}
              Pause now
            </button>
          </section>
        </div>
      )}
    </section>
  );
}

function SupportPage({ session }: { session: Session }) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const query = useQuery({
    queryKey: ["admin-support"],
    queryFn: () => api<SupportRow[]>("/api/admin/support", session),
  });
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (query.data ?? []).filter((item) => {
      const statusMatches =
        status === "all" ||
        (status === "active" ? item.status !== "resolved" : item.status === status);
      const searchMatches =
        !needle ||
        item.id.toLowerCase().includes(needle) ||
        item.subject?.toLowerCase().includes(needle) ||
        item.description.toLowerCase().includes(needle) ||
        item.contact_email?.toLowerCase().includes(needle);
      return statusMatches && searchMatches;
    });
  }, [query.data, search, status]);
  useEffect(() => {
    if (!rows.some((item) => item.id === selectedId)) setSelectedId(rows[0]?.id ?? null);
  }, [rows, selectedId]);
  const detail = useQuery({
    queryKey: ["admin-support", selectedId],
    queryFn: () => api<SupportDetail>(`/api/admin/support/${selectedId}`, session),
    enabled: Boolean(selectedId),
  });
  const reply = useMutation({
    mutationFn: () => {
      if (!selectedId) throw new Error("Choose a request");
      return api(`/api/admin/support/${selectedId}/reply`, session, {
        method: "POST",
        body: JSON.stringify({ message }),
      });
    },
    onSuccess: async () => {
      setMessage("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-support"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-support", selectedId] }),
      ]);
    },
  });
  const updateStatus = useMutation({
    mutationFn: (nextStatus: "open" | "resolved") => {
      if (!selectedId) throw new Error("Choose a request");
      return api(`/api/admin/support/${selectedId}/status`, session, {
        method: "POST",
        body: JSON.stringify({ status: nextStatus }),
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-support"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-support", selectedId] }),
      ]);
    },
  });
  const selected = detail.data?.ticket;
  return (
    <section>
      <Header
        eyebrow="PLAYER CARE"
        title="Support inbox"
        copy="Review the complete player conversation, track every acknowledgment and reply, and close only when the issue is resolved."
      />
      {query.error && <div className="error">{query.error.message}</div>}
      <div className="support-toolbar">
        <label>
          <Search />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search subject, email, message, or ticket ID"
          />
        </label>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="active">Open & reviewing</option>
          <option value="open">Open</option>
          <option value="reviewing">Reviewing</option>
          <option value="resolved">Closed</option>
          <option value="all">All conversations</option>
        </select>
      </div>
      <div className="support-inbox">
        <aside className="support-queue" aria-label="Support conversations">
          {rows.map((item) => (
            <button
              className={item.id === selectedId ? "selected" : ""}
              key={item.id}
              type="button"
              onClick={() => {
                setSelectedId(item.id);
                setMessage("");
              }}
            >
              <span>
                <StatePill value={item.kind} />
                <StatePill value={item.status} />
              </span>
              <strong>{item.subject ?? `${item.kind} request`}</strong>
              <p>{item.description}</p>
              <small>
                {item.contact_email ?? "Account email"} · {formatLocalDateTime(item.updated_at)}
              </small>
            </button>
          ))}
          {!query.isLoading && !rows.length && (
            <div className="empty support-empty">
              <CheckCircle2 />
              <strong>No matching conversations</strong>
            </div>
          )}
        </aside>
        <section className="support-thread" aria-live="polite">
          {detail.isLoading && (
            <div className="empty large">
              <LoaderCircle className="spin" />
              <p>Loading conversation…</p>
            </div>
          )}
          {detail.error && <div className="error">{detail.error.message}</div>}
          {selected && (
            <>
              <header>
                <div>
                  <small>TICKET #{selected.id.slice(0, 8).toUpperCase()}</small>
                  <h2>{selected.subject ?? `${selected.kind} request`}</h2>
                  <p>
                    {selected.contact_email ?? "No reply email"}
                    {detail.data?.ticket.evidence?.visitor_context?.contact_phone
                      ? ` · ${detail.data.ticket.evidence.visitor_context.contact_phone}`
                      : ""}
                  </p>
                  {detail.data?.ticket.evidence?.visitor_context && (
                    <small className="support-visitor-context">
                      {[
                        detail.data.ticket.evidence.visitor_context.city,
                        detail.data.ticket.evidence.visitor_context.region,
                        detail.data.ticket.evidence.visitor_context.country_code,
                        detail.data.ticket.evidence.visitor_context.timezone,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Location unavailable"}
                      {detail.data.ticket.evidence.visitor_context.source
                        ? ` · ${detail.data.ticket.evidence.visitor_context.source}`
                        : ""}
                    </small>
                  )}
                  {detail.data?.ticket.evidence?.visitor_context?.client_context && (
                    <small className="support-visitor-context">
                      {[
                        detail.data.ticket.evidence.visitor_context.client_context.source,
                        detail.data.ticket.evidence.visitor_context.client_context.gameId,
                        detail.data.ticket.evidence.visitor_context.client_context.roomCode
                          ? `room ${detail.data.ticket.evidence.visitor_context.client_context.roomCode}`
                          : null,
                        detail.data.ticket.evidence.visitor_context.client_context.viewportWidth &&
                        detail.data.ticket.evidence.visitor_context.client_context.viewportHeight
                          ? `${detail.data.ticket.evidence.visitor_context.client_context.viewportWidth}×${detail.data.ticket.evidence.visitor_context.client_context.viewportHeight} viewport`
                          : null,
                        detail.data.ticket.evidence.visitor_context.client_context.connectionType,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      {detail.data.ticket.evidence.visitor_context.client_context.userAgent
                        ? ` · ${detail.data.ticket.evidence.visitor_context.client_context.userAgent}`
                        : ""}
                    </small>
                  )}
                </div>
                <div className="support-thread-pills">
                  <StatePill value={selected.priority} />
                  <StatePill value={selected.status} />
                </div>
              </header>
              <div className="support-messages">
                {(detail.data?.messages ?? []).map((item) => (
                  <article className={`support-message ${item.author_type}`} key={item.id}>
                    <div>
                      <strong>
                        {item.author_type === "customer"
                          ? (selected.contact_email ?? "Player")
                          : item.author_type === "agent"
                            ? "Topzle Support"
                            : "Topzle automation"}
                      </strong>
                      <small>{formatLocalDateTime(item.created_at)}</small>
                    </div>
                    <p>{item.body}</p>
                    {item.email_status !== "not_applicable" && (
                      <footer className={`email-delivery ${item.email_status}`}>
                        <Mail />
                        {item.email_status === "accepted"
                          ? "Accepted by mail server"
                          : item.email_status === "queued"
                            ? "Queued for email"
                            : "Email delivery failed"}
                        {item.email_to ? ` · ${item.email_to}` : ""}
                        {item.email_message_id && <code>{item.email_message_id}</code>}
                        {item.email_error && <span>{item.email_error}</span>}
                      </footer>
                    )}
                  </article>
                ))}
              </div>
              <div className="support-composer">
                <label>
                  Reply by email
                  <textarea
                    className="reply-editor"
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder="Write a clear, helpful response…"
                  />
                </label>
                {(reply.error || updateStatus.error) && (
                  <div className="error">{reply.error?.message ?? updateStatus.error?.message}</div>
                )}
                <div className="support-composer-actions">
                  <button
                    type="button"
                    className="primary-action"
                    disabled={
                      reply.isPending || updateStatus.isPending || message.trim().length < 3
                    }
                    onClick={() => reply.mutate()}
                  >
                    {reply.isPending ? <LoaderCircle className="spin" /> : <Send />}
                    {selected.status === "resolved" ? "Send and reopen" : "Send reply"}
                  </button>
                  <button
                    type="button"
                    className="support-status-action"
                    disabled={reply.isPending || updateStatus.isPending}
                    onClick={() =>
                      updateStatus.mutate(selected.status === "resolved" ? "open" : "resolved")
                    }
                  >
                    {updateStatus.isPending ? (
                      <LoaderCircle className="spin" />
                    ) : selected.status === "resolved" ? (
                      <PlayCircle />
                    ) : (
                      <CheckCircle2 />
                    )}
                    {selected.status === "resolved" ? "Reopen" : "Mark closed"}
                  </button>
                </div>
              </div>
            </>
          )}
          {!selected && !detail.isLoading && (
            <div className="empty large">
              <MessageSquareText />
              <h2>Select a conversation</h2>
              <p>Choose a support request to see its complete history.</p>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}

function AdministratorsPage({ session }: { session: Session }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdministratorsData["administrators"][number] | null>(null);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("read_only");
  const [active, setActive] = useState(true);
  const query = useQuery({
    queryKey: ["administrators"],
    queryFn: () => api<AdministratorsData>("/api/admin/administrators", session),
  });
  const add = useMutation({
    mutationFn: () =>
      api("/api/admin/administrators", session, {
        method: "POST",
        body: JSON.stringify({ email, displayName, role }),
      }),
    onSuccess: async () => {
      setOpen(false);
      setEmail("");
      setDisplayName("");
      await queryClient.invalidateQueries({ queryKey: ["administrators"] });
    },
  });
  const update = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error("Choose an administrator");
      return api(`/api/admin/administrators/${editing.user_id}`, session, {
        method: "PATCH",
        body: JSON.stringify({ role, active }),
      });
    },
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ["administrators"] });
    },
  });
  return (
    <section>
      <Header
        eyebrow="ACCESS CONTROL"
        title="Administrators"
        copy="Assign the smallest role needed. Role changes and privileged actions are recorded in the audit log."
      />
      <div className="page-actions">
        <button className="primary-action" type="button" onClick={() => setOpen(true)}>
          <UserPlus /> Add administrator
        </button>
      </div>
      {query.error && <div className="error">{query.error.message}</div>}
      <div className="admin-roster">
        {(query.data?.administrators ?? []).map((administrator) => (
          <article key={administrator.user_id}>
            <span>{administrator.profiles.display_name.slice(0, 1).toUpperCase()}</span>
            <div>
              <h2>{administrator.profiles.display_name}</h2>
              <p>{administrator.profiles.email ?? "No email"}</p>
            </div>
            <StatePill value={administrator.admin_roles.key} />
            <small>{administrator.admin_roles.description}</small>
            <button
              type="button"
              className="secondary-action"
              onClick={() => {
                setEditing(administrator);
                setRole(administrator.admin_roles.key);
                setActive(administrator.active);
              }}
            >
              Manage
            </button>
          </article>
        ))}
      </div>
      {open && (
        <div className="modal-backdrop">
          <section className="admin-modal" role="dialog" aria-modal="true">
            <button className="modal-close" type="button" onClick={() => setOpen(false)}>
              <X />
            </button>
            <small>NEW ADMINISTRATOR</small>
            <h2>Assign access</h2>
            <p>
              If this email is new, an account is created. They can sign in with the same one-time
              email flow used by other administrators.
            </p>
            <label>
              Name
              <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
            </label>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label>
              Role
              <select value={role} onChange={(event) => setRole(event.target.value)}>
                {(query.data?.roles ?? []).map((candidate) => (
                  <option key={candidate.id} value={candidate.key}>
                    {candidate.key.replaceAll("_", " ")} — {candidate.description}
                  </option>
                ))}
              </select>
            </label>
            {add.error && <div className="error">{add.error.message}</div>}
            <button
              type="button"
              className="primary-action"
              disabled={add.isPending || displayName.trim().length < 2 || !email.includes("@")}
              onClick={() => add.mutate()}
            >
              {add.isPending ? <LoaderCircle className="spin" /> : <UserPlus />}
              Add administrator
            </button>
          </section>
        </div>
      )}
      {editing && (
        <div className="modal-backdrop">
          <section className="admin-modal" role="dialog" aria-modal="true">
            <button className="modal-close" type="button" onClick={() => setEditing(null)}>
              <X />
            </button>
            <small>ADMINISTRATOR ACCESS</small>
            <h2>{editing.profiles.display_name}</h2>
            <label>
              Role
              <select value={role} onChange={(event) => setRole(event.target.value)}>
                {(query.data?.roles ?? []).map((candidate) => (
                  <option key={candidate.id} value={candidate.key}>
                    {candidate.key.replaceAll("_", " ")} — {candidate.description}
                  </option>
                ))}
              </select>
            </label>
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
              />
              Administrator access active
            </label>
            <p>
              Disabling access removes the trusted administrator role immediately. Historical audit
              records remain intact.
            </p>
            {update.error && <div className="error">{update.error.message}</div>}
            <button
              type="button"
              className={active ? "primary-action" : "danger-action"}
              disabled={update.isPending}
              onClick={() => update.mutate()}
            >
              {update.isPending ? <LoaderCircle className="spin" /> : <ShieldCheck />}
              Save access
            </button>
          </section>
        </div>
      )}
    </section>
  );
}

function Reports({ session }: { session: Session }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["reports"],
    queryFn: () => api<ReportRow[]>("/api/admin/reports", session),
  });
  const updateReport = useMutation({
    mutationFn: (input: { id: string; status: "reviewing" | "resolved"; resolution?: string }) =>
      api<ReportRow>(`/api/admin/reports/${input.id}`, session, {
        method: "PATCH",
        body: JSON.stringify({
          status: input.status,
          ...(input.resolution ? { resolution: input.resolution } : {}),
        }),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reports"] }),
        queryClient.invalidateQueries({ queryKey: ["overview"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-safety"] }),
      ]);
    },
  });
  const columns = useMemo(() => {
    const helper = createColumnHelper<ReportRow>();
    return [
      helper.accessor("created_at", {
        header: "Received",
        cell: (cell) => formatLocalDateTime(cell.getValue()),
      }),
      helper.accessor("category", { header: "Category" }),
      helper.accessor("description", {
        header: "Concern",
        cell: (cell) => cell.getValue() ?? "No description",
      }),
      helper.accessor("target_type", { header: "Target" }),
      helper.accessor("priority", {
        header: "Priority",
        cell: (cell) => cell.getValue() ?? "normal",
      }),
      helper.accessor("status", { header: "Status" }),
      helper.display({
        id: "action",
        header: "",
        cell: (cell) => (
          <button
            type="button"
            className="review"
            disabled={updateReport.isPending}
            onClick={() => {
              const report = cell.row.original;
              updateReport.mutate(
                report.status === "open"
                  ? { id: report.id, status: "reviewing" }
                  : {
                      id: report.id,
                      status: "resolved",
                      resolution: "Reviewed and resolved by an authorized administrator.",
                    },
              );
            }}
          >
            {updateReport.isPending
              ? "Saving…"
              : cell.row.original.status === "open"
                ? "Start review"
                : "Resolve"}
          </button>
        ),
      }),
    ];
  }, [updateReport]);
  const table = useReactTable({
    data: query.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <section>
      <Header
        eyebrow="TRUST & SAFETY"
        title="Moderation queue"
        copy="Reported evidence only. Private calls and messages remain inaccessible unless retained and authorized for review."
      />
      <div className="toolbar">
        <div>
          <Search />
          <input placeholder="Search reports" />
        </div>
        <button type="button">Priority: all</button>
        <button type="button">Status: open</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
            {!query.isLoading && table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <div className="empty">
                    <ShieldCheck />
                    No reports are waiting in this environment.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function Header({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return (
    <header className="page-head">
      <div>
        <small>{eyebrow}</small>
        <h1>{title}</h1>
        <p>{copy}</p>
      </div>
      <span>
        Production <i />
      </span>
    </header>
  );
}
function Overview({ session }: { session: Session }) {
  const [days, setDays] = useState(7);
  const range = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - days * 86_400_000);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [days]);
  const query = useQuery({
    queryKey: ["analytics", days],
    queryFn: () =>
      api<AnalyticsData>(
        `/api/admin/analytics?start=${encodeURIComponent(range.start)}&end=${encodeURIComponent(range.end)}`,
        session,
      ),
    refetchInterval: 30_000,
  });
  const metricGroups = [
    {
      label: "Daily active users",
      value: query.data?.dau,
      delta: `${query.data?.mau ?? 0} monthly active`,
      icon: Users,
    },
    {
      label: "Games played",
      value: query.data?.totalGames,
      delta: `${query.data?.completedGames ?? 0} completed`,
      icon: Gamepad2,
    },
    {
      label: "Rooms created",
      value: query.data?.roomsCreated,
      delta: `During this ${days}-day period`,
      icon: Boxes,
    },
    {
      label: "Completion rate",
      value: `${query.data?.completionRate ?? 0}%`,
      delta: "Authoritative completed sessions",
      icon: CheckCircle2,
    },
  ];
  const maximumDaily = Math.max(
    1,
    ...(query.data?.daily ?? []).map((point) =>
      Math.max(point.games, point.rooms, point.active_users),
    ),
  );
  return (
    <section>
      <Header
        eyebrow="LIVE OPERATIONS"
        title="What’s happening"
        copy="Real play, room, and active-user totals from the selected period."
      />
      <fieldset className="range-picker">
        <legend>Analytics time range</legend>
        {[7, 30, 90].map((option) => (
          <button
            type="button"
            className={days === option ? "active" : ""}
            onClick={() => setDays(option)}
            key={option}
          >
            {option} days
          </button>
        ))}
      </fieldset>
      {query.error && <div className="error">{query.error.message}</div>}
      <div className="metrics">
        {metricGroups.map(({ label, value, delta, icon: Icon }) => (
          <article key={label}>
            <Icon />
            <small>{label}</small>
            <strong>{query.isLoading ? "…" : (value ?? "—")}</strong>
            <span>{delta}</span>
          </article>
        ))}
      </div>
      <div className="ops-grid">
        <article>
          <div className="card-head">
            <div>
              <small>ROOM ACTIVITY</small>
              <h2>Daily activity</h2>
            </div>
          </div>
          <div className="analytics-chart">
            {(query.data?.daily ?? []).map((point) => (
              <div key={point.date} title={`${point.date}: ${point.games} games`}>
                <span
                  className="active-users"
                  style={{ height: `${(point.active_users / maximumDaily) * 100}%` }}
                />
                <span
                  className="games"
                  style={{ height: `${(point.games / maximumDaily) * 100}%` }}
                />
                <small>
                  {new Date(`${point.date}T00:00:00`).toLocaleDateString(undefined, {
                    day: "numeric",
                  })}
                </small>
              </div>
            ))}
          </div>
          <div className="chart-legend">
            <span>
              <i className="users-dot" /> Active users
            </span>
            <span>
              <i className="games-dot" /> Games
            </span>
          </div>
        </article>
        <article>
          <div className="card-head">
            <div>
              <small>GAME MIX</small>
              <h2>Most played</h2>
            </div>
          </div>
          <div className="game-breakdown">
            {(query.data?.byGame ?? []).map((game) => (
              <article key={game.game_id}>
                <div>
                  <strong>{game.name}</strong>
                  <small>{game.completed} completed</small>
                </div>
                <b>{game.plays}</b>
                <span
                  style={{
                    width: `${Math.max(4, (game.plays / Math.max(1, query.data?.totalGames ?? 1)) * 100)}%`,
                  }}
                />
              </article>
            ))}
            {!query.isLoading && !query.data?.byGame.length && <p>No games in this period.</p>}
          </div>
        </article>
      </div>
    </section>
  );
}
function Placeholder({ title, icon: Icon }: { title: string; icon: typeof Users }) {
  return (
    <section>
      <Header
        eyebrow="MANAGEMENT"
        title={title}
        copy="The authorization boundary, navigation, empty states and audit-ready action surface are connected. Configure Supabase to load production data."
      />
      <div className="empty large">
        <Icon />
        <h2>No production data connected</h2>
        <p>
          Apply migrations, configure Worker secrets, then this workspace will populate through
          permission-checked admin routes.
        </p>
      </div>
    </section>
  );
}
function Shell({ session, onLogout }: { session: Session; onLogout: () => void }) {
  const [page, setPage] = useState("overview");
  const role = String(session.user.app_metadata.admin_role ?? "read_only");
  const allowedPages: Record<string, string[]> = {
    super_admin: [
      "overview",
      "users",
      "rooms",
      "safety",
      "moderation",
      "support",
      "controls",
      "administrators",
    ],
    operations_admin: ["overview", "rooms", "moderation", "support", "controls"],
    moderator: ["overview", "users", "rooms", "safety", "moderation"],
    support_agent: ["overview", "users", "rooms", "moderation", "support"],
    analyst: ["overview"],
    read_only: ["overview", "users", "rooms", "moderation", "controls"],
  };
  const nav = allowedPages[role] ?? ["overview"];
  const icons = {
    overview: LayoutDashboard,
    users: Users,
    rooms: Boxes,
    safety: ShieldCheck,
    moderation: ShieldCheck,
    support: Mail,
    controls: Settings2,
    administrators: KeyRound,
  };
  return (
    <div className="shell">
      <aside>
        <div className="logo">
          <BrandMark />
          <div>
            <b>Topzle</b>
            <small>OPERATIONS</small>
          </div>
        </div>
        <nav>
          {nav.map((item) => {
            const Icon = icons[item as keyof typeof icons] ?? LayoutDashboard;
            return (
              <NavLink
                className={page === item ? "active" : ""}
                to={`#${item}`}
                onClick={() => setPage(item)}
                key={item}
              >
                <Icon />
                {item}
              </NavLink>
            );
          })}
        </nav>
        <div className="operator">
          <span>{session.user.email?.slice(0, 1).toUpperCase()}</span>
          <div>
            <b>{session.user.email}</b>
            <small>{role.replaceAll("_", " ")}</small>
          </div>
          <button type="button" onClick={onLogout}>
            <LogOut />
          </button>
        </div>
      </aside>
      <main>
        {page === "overview" ? (
          <Overview session={session} />
        ) : page === "users" ? (
          <UsersPage session={session} />
        ) : page === "rooms" ? (
          <RoomsPage session={session} role={role} />
        ) : page === "safety" ? (
          <SafetyCenter session={session} />
        ) : page === "moderation" ? (
          <Reports session={session} />
        ) : page === "support" ? (
          <SupportPage session={session} />
        ) : page === "controls" ? (
          <OperationsPage session={session} />
        ) : page === "administrators" ? (
          <AdministratorsPage session={session} />
        ) : (
          <Placeholder
            title={page[0]?.toUpperCase() + page.slice(1)}
            icon={icons[page as keyof typeof icons] ?? Settings2}
          />
        )}
      </main>
    </div>
  );
}
function App() {
  const [session, setSession] = useState<Session | null>(null);
  useEffect(() => {
    void supabase?.auth.getSession().then(({ data }) => setSession(data.session));
    const listener = supabase?.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => listener?.data.subscription.unsubscribe();
  }, []);
  return session?.user.app_metadata.admin_role ? (
    <Shell session={session} onLogout={() => void supabase?.auth.signOut()} />
  ) : session ? (
    <AccessDenied session={session} />
  ) : (
    <Login onSession={setSession} />
  );
}
const client = new QueryClient();
const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Topzle Admin root element is missing");
createRoot(rootElement).render(
  <QueryClientProvider client={client}>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </QueryClientProvider>,
);
